/**
 * One application, from the provider's side: the data pack, the eligibility
 * evaluation that produced it, and the decision.
 *
 * This is the surface where the platform's claims are either true or decorative,
 * so three of them are visible on it.
 *
 * The pack passed the consent gateway before it was built. A provider has no
 * privileged path to customer data; it passes the same gateway a human user
 * passes, which is the only way the disclosure register can be complete. Where
 * the gateway refused, this screen is not reached at all: the server answers
 * with the refusal rather than with an empty pack.
 *
 * The evaluation is three-valued. A rule the data could not answer is
 * indeterminate, not failed, and both are listed separately so a decline on
 * missing data is a deliberate act rather than an artefact.
 *
 * A decline carries a code from a published list. Codes that would reveal the
 * provider's criteria are marked on the code itself and are not shown to the
 * applicant, which is a property of the code list rather than a judgement made
 * at decline time.
 */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { api } from '../../api/client'
import type { Dict, SectionMeta } from '../../api/types'
import { useSession } from '../../app/session'
import { appPath } from '../../app/paths'
import { AttributeTable } from '../../components/Attributes'
import type { Provenance } from '../../components/Attributes'
import { Badge, Empty, KV, Note, toneOf } from '../../components/bits'
import { PageHead } from '../../components/PageHead'
import { Panel, Cols } from '../../components/Panel'

export function ApplicationRecord({ data }: { data: Dict }) {
  const { n, amount, dt, lbl, can } = useSession()
  const navigate = useNavigate()
  const a = data.a as Dict
  const section = data.section as SectionMeta
  const ev = data.ev as Dict | null
  const offers = (data.offers || []) as Dict[]
  const declineCodes = (data.decline_codes || []) as Dict[]
  const decision = data.decision as Dict | null

  const [outcome, setOutcome] = useState<'approved' | 'declined' | 'info_requested'>('approved')
  const [code, setCode] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const decide = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await api.act(`/app/applications/${a.application_id}/decide`, {
        decision: outcome,
        decline_reason_code: outcome === 'declined' ? code : '',
        note,
      })
      const url = new URL(result.redirect, window.location.origin)
      const refused = url.searchParams.get('error')
      if (refused) { setError(refused); return }
      navigate(0)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const decided = Boolean(a.decision)
  const failed = (ev?.failed_rules || []) as string[]
  const indeterminate = (ev?.indeterminate_rules || []) as string[]
  const missing = (ev?.missing_keys || []) as string[]

  return (
    <>
      <PageHead section={section} title={a.application_ref as string}
                auditLink={data.audit_link as string} />

      {error && <Note kind="bad"><b>Not recorded.</b> {error}</Note>}

      {decision && (
        <Note kind="good">
          <b>The consent gateway allowed this pack.</b> The disclosure was logged
          against the applicant's consent grant, with the purpose and the scope
          set it covered.
        </Note>
      )}

      <Cols>
        <Panel title="The request" explain="application.record">
          <div className="big-value">
            <b className="num">{amount(a.amount_requested)}</b>
            <span className="muted">{lbl(a.economic_purpose)}</span>
          </div>
          <KV rows={[
            ['Applicant', <>
              {a.display_name_en as string}{' '}
              <span className="xs muted">({a.customer_ref as string})</span>
            </>],
            ['Kind', <Badge tone="muted" key="k">{lbl(a.customer_type)}</Badge>],
            ['Size', a.size_classification
              ? <Badge tone="muted" key="s">{lbl(a.size_classification)}</Badge>
              : '—'],
            ['Region', (a.hq_region as string) || '—'],
            ['Registry number', a.cr_number
              ? <code key="cr">{a.cr_number as string}</code> : '—'],
            ['Tenor sought', a.tenor_months ? `${n(a.tenor_months)} months` : 'unstated'],
            ['Compliance required', a.compliance_required
              ? <Badge tone={toneOf(a.compliance_required)} key="c">
                  {lbl(a.compliance_required)}
                </Badge>
              : 'either'],
            ['Urgency', a.urgency_days ? `${n(a.urgency_days)} days` : 'unstated'],
            ...(a.purpose_text
              ? [['In the applicant\'s words', String(a.purpose_text)] as [string, string]]
              : []),
          ]} />
        </Panel>

        <Panel title="Your product and its state" explain="application.record">
          <KV rows={[
            ['Product', (a.product_name as string) || '—'],
            ['Compliance', a.compliance_type
              ? <Badge tone={toneOf(a.compliance_type)} key="ct">
                  {lbl(a.compliance_type)}
                </Badge>
              : '—'],
            ...(a.sharia_structure
              ? [['Structure', lbl(a.sharia_structure)] as [string, string]] : []),
            ...(a.sharia_board_name
              ? [['Sharia board', String(a.sharia_board_name)] as [string, string]] : []),
            ...(a.sharia_fatwa_ref
              ? [['Fatwa reference', String(a.sharia_fatwa_ref)] as [string, string]] : []),
            ['State', <Badge tone={toneOf(a.status)} key="st">{lbl(a.status)}</Badge>],
            ['Routed', a.routed_at ? dt(a.routed_at) : '—'],
            ...(a.decision_at
              ? [['Decided', dt(a.decision_at)] as [string, string]] : []),
            ...(a.decision
              ? [['Decision', <Badge tone={toneOf(a.decision)} key="d">
                  {lbl(a.decision)}</Badge>] as [string, JSX.Element]]
              : []),
            ...(a.decline_reason_code
              ? [['Reason', `${lbl(a.decline_reason_code)}`] as [string, string]] : []),
          ]} />
        </Panel>
      </Cols>

      <Panel title="The data pack" explain="application.data_pack" tight>
        <AttributeTable attrs={(data.attrs || {}) as Record<string, unknown>}
                        provenance={(data.provenance || {}) as Record<string, Provenance>}
                        definitions={(data.definitions || {}) as Record<string, Dict>}
                        stale={(data.stale || []) as string[]}
                        withheld={(data.withheld || []) as string[]} />
      </Panel>

      <Panel title="Eligibility, as evaluated" explain="rules.evaluations">
        {ev ? (
          <>
            <div className="scope-note">
              <Badge tone={toneOf(ev.outcome)}>{lbl(ev.outcome)}</Badge>
              <Badge tone="muted">
                rule set {String(ev.ruleset_id)} v{String(ev.ruleset_version)}
              </Badge>
              <Badge tone="muted">engine {String(ev.engine_version)}</Badge>
              <Badge tone="muted">{n(ev.duration_ms)} ms</Badge>
              <Badge tone="muted">evaluated {dt(ev.evaluated_at)}</Badge>
            </div>

            <Cols>
              <div>
                <h4>Rules that failed</h4>
                {failed.length === 0 ? (
                  <p className="muted">None.</p>
                ) : (
                  <div className="srcs">
                    {failed.map((r) => <code key={r}>{r}</code>)}
                  </div>
                )}
              </div>
              <div>
                <h4>Rules the data could not answer</h4>
                {indeterminate.length === 0 ? (
                  <p className="muted">None.</p>
                ) : (
                  <div className="srcs">
                    {indeterminate.map((r) => <code key={r}>{r}</code>)}
                  </div>
                )}
              </div>
            </Cols>

            {missing.length > 0 && (
              <div className="cav">
                <b>Attributes the rule set wanted and did not have.</b>{' '}
                {missing.map((k) => <code key={k} style={{ marginInlineEnd: 5 }}>{k}</code>)}
                <div className="xs muted">
                  An absent attribute leaves its rule indeterminate. It is not a
                  failure, and an application with indeterminate rules can be
                  approved on what is known.
                </div>
              </div>
            )}

            <p className="xs muted">
              The attribute set is hashed and stored with the evaluation, and the
              rule set version is pinned, so this outcome can be replayed years
              from now against the same inputs and the same rules.{' '}
              {ev.attributes_hash
                ? <code>{String(ev.attributes_hash).slice(0, 16)}</code> : null}
            </p>

            {ev.evaluation_id && (
              <Link className="btn sm"
                    to={appPath(`/app/evaluations/${ev.evaluation_id}`)}>
                Open the evaluation rule by rule
              </Link>
            )}
          </>
        ) : (
          <Empty title="No evaluation is attached">
            An application routed without an evaluation is a routing defect
            rather than a product without rules: a product cannot be published
            without a rule set.
          </Empty>
        )}
      </Panel>

      {offers.length > 0 && (
        <Panel title={`Offers you have made · ${offers.length}`} explain="offer.list" tight>
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th className="num">Amount</th><th className="num">Tenor</th>
                  <th className="num">Total cost of finance</th>
                  <th>State</th><th>Expires</th><th>Made</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((o) => (
                  <tr key={String(o.offer_id)}>
                    <td data-label="Amount" className="num">
                      {amount(o.amount_offered, o.currency_code as string)}
                    </td>
                    <td data-label="Tenor" className="num">{n(o.tenor_months)} mo</td>
                    <td data-label="Total cost of finance" className="num">
                      {amount(o.total_cost_of_finance, o.currency_code as string)}
                    </td>
                    <td data-label="State">
                      <Badge tone={toneOf(o.status)}>{lbl(o.status)}</Badge>
                    </td>
                    <td data-label="Expires">{o.expires_at ? dt(o.expires_at) : '—'}</td>
                    <td data-label="Made">{dt(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {can('application.record', 'approve') && !decided && (
        <Panel title="Record your decision" explain="application.decisions">
          <div className="inline-form">
            <div className="field">
              <label htmlFor="outcome">Decision</label>
              <select id="outcome" value={outcome}
                      onChange={(e) => setOutcome(e.target.value as never)}>
                <option value="approved">Approve and offer</option>
                <option value="info_requested">Ask the applicant for more</option>
                <option value="declined">Decline</option>
              </select>
            </div>

            {outcome === 'declined' && (
              <div className="field wide">
                <label htmlFor="code">Reason, from the published list</label>
                <select id="code" value={code} onChange={(e) => setCode(e.target.value)}>
                  <option value="">Choose a reason</option>
                  {declineCodes.map((c) => (
                    <option value={c.decline_code as string} key={c.decline_code as string}>
                      {c.name_en as string}
                      {c.is_remediable ? ' · remediable' : ' · structural'}
                      {c.reveals_criteria ? ' · not shown to the applicant' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="field wide">
              <label htmlFor="note">Note</label>
              <input id="note" value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="What the applicant should be told, in a sentence" />
            </div>

            <button className="btn primary" type="button"
                    disabled={busy || (outcome === 'declined' && !code)}
                    onClick={() => void decide()}>
              {busy ? 'Recording…' : 'Record the decision'}
            </button>
          </div>

          <p className="xs muted">
            A decline needs a code before the schema accepts it, so an unexplained
            decline cannot be recorded. Where the rule set is marked as requiring
            a second pair of eyes, this records your decision and the application
            waits for the second: that constraint is in the database, not in this
            screen.
          </p>
        </Panel>
      )}

      {decided && (
        <Panel title="Why this decision cannot be edited" explain="audit.record_trail">
          <p className="muted">
            A decision is a record of what was decided at a time, on data that was
            true then. Correcting one in place would leave the applicant's copy,
            the outbox event the provider's systems consumed and the audit trail
            describing three different decisions. A changed mind is recorded as a
            new decision with its own reason.
          </p>
        </Panel>
      )}
    </>
  )
}
